import { c as _c } from "react/compiler-runtime";
import CustomTooltip from "@/components/utils/tooltip/CustomTooltip";
import type { ApiMintMetrics } from "@/generated/models/ApiMintMetrics";
import type { ReactNode } from "react";
import { formatNumberWithCommas } from "../utils/formatNumbers";
interface MintMetricsCardProps {
  readonly data: ApiMintMetrics[];
  readonly icon: ReactNode;
}
function MintBarChart(t0) {
  const $ = _c(20);
  const {
    data
  } = t0;
  if (data.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== data) {
    t1 = Math.max(...data.map(_temp));
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const maxTotal = t1;
  const gridTemplate = `repeat(${data.length}, minmax(2px, 1fr))`;
  let t2;
  if ($[2] !== gridTemplate) {
    t2 = {
      columnGap: "clamp(2px, 0.5vw, 8px)",
      gridTemplateColumns: gridTemplate
    };
    $[2] = gridTemplate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== data || $[5] !== maxTotal) {
    let t4;
    if ($[7] !== maxTotal) {
      t4 = item => {
        const total = item.subscriptions + item.mints;
        const totalHeight = maxTotal > 0 ? total / maxTotal * 100 : 0;
        const mintProportion = total > 0 ? item.mints / total * 100 : 0;
        const subProportion = total > 0 ? item.subscriptions / total * 100 : 0;
        return <CustomTooltip key={item.card} content={<div className="tw-text-xs"><div className="tw-mb-1 tw-font-semibold">Card #{item.card}</div><div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-size-2 tw-rounded-full tw-bg-emerald-500" /><span>Mints: {formatNumberWithCommas(item.mints)}</span></div><div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-size-2 tw-rounded-full tw-bg-blue-500" /><span>Subscriptions:{" "}{formatNumberWithCommas(item.subscriptions)}</span></div><div className="tw-mt-1 tw-border-t tw-border-neutral-600 tw-pt-1">Total: {formatNumberWithCommas(total)}</div></div>} placement="top"><div className="tw-flex tw-cursor-default tw-flex-col tw-overflow-hidden tw-rounded-t tw-transition-all hover:tw-opacity-80" style={{
            height: `${Math.max(totalHeight, 3)}%`
          }}><div className="tw-w-full tw-bg-emerald-500" style={{
              height: `${mintProportion}%`
            }} /><div className="tw-w-full tw-bg-blue-500" style={{
              height: `${subProportion}%`
            }} /></div></CustomTooltip>;
      };
      $[7] = maxTotal;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = data.map(t4);
    $[4] = data;
    $[5] = maxTotal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className="tw-grid tw-h-16 tw-items-end" style={t2}>{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== data || $[13] !== gridTemplate) {
    t5 = data.length <= 10 && <div className="tw-grid" style={{
      columnGap: "clamp(2px, 0.5vw, 8px)",
      gridTemplateColumns: gridTemplate
    }}>{data.map(_temp2)}</div>;
    $[12] = data;
    $[13] = gridTemplate;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-size-2 tw-rounded-full tw-bg-emerald-500" /><span>Mints</span></div>;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-xs tw-text-neutral-400">{t6}<div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-size-2 tw-rounded-full tw-bg-blue-500" /><span>Subscriptions</span></div></div>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t5) {
    t8 = <div className="tw-flex tw-flex-col tw-gap-2">{t4}{t5}{t7}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp2(item_0) {
  return <div key={item_0.card} className="tw-flex-1 tw-text-center tw-text-[10px] tw-text-neutral-500">#{item_0.card}</div>;
}
function _temp(d) {
  return d.subscriptions + d.mints;
}
export default function MintMetricsCard(t0) {
  const $ = _c(25);
  const {
    data,
    icon
  } = t0;
  const latestMint = data[0];
  if (data.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-col-span-full tw-rounded-xl tw-border tw-border-neutral-800 tw-bg-[#0f1318] tw-p-5"><p className="tw-text-sm tw-text-neutral-400">No mint data available</p></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-flex tw-items-center tw-gap-3"><h3 className="tw-text-base tw-font-semibold tw-text-white">Mint Stats</h3><div className="tw-flex tw-gap-1" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon) {
    t2 = <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between">{t1}<div className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-lg tw-bg-emerald-500">{icon}</div></div>;
    $[2] = icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-emerald-400">Latest Mint</p>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== latestMint) {
    t4 = latestMint && <><p className="tw-mb-3 tw-text-sm tw-text-neutral-400">Card #{latestMint.card}</p><div className="tw-flex tw-gap-6"><div><p className="tw-mb-0.5 tw-text-xs tw-text-neutral-500">Mints</p><p className="tw-text-2xl tw-font-bold tw-text-white">{formatNumberWithCommas(latestMint.mints)}</p></div><div><p className="tw-mb-0.5 tw-text-xs tw-text-neutral-500">Subscriptions</p><p className="tw-text-2xl tw-font-bold tw-text-white">{formatNumberWithCommas(latestMint.subscriptions)}</p></div></div></>;
    $[5] = latestMint;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="tw-flex-shrink-0">{t3}{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-hidden tw-w-px tw-bg-iron-700/50 sm:tw-block" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== data.length) {
    t7 = <p className="tw-mb-3 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-emerald-400">Last {data.length} Mints</p>;
    $[10] = data.length;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== data) {
    t8 = data.toReversed();
    $[12] = data;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <MintBarChart data={t8} />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <div className="tw-min-w-0 tw-flex-1">{t7}{t9}</div>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t5) {
    t11 = <div className="tw-flex tw-flex-col tw-gap-6 sm:tw-flex-row sm:tw-gap-8">{t5}{t6}{t10}</div>;
    $[19] = t10;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t2) {
    t12 = <div className="tw-col-span-full tw-rounded-xl tw-border tw-border-neutral-800 tw-bg-[#0f1318] tw-p-5">{t2}{t11}</div>;
    $[22] = t11;
    $[23] = t2;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}